@file:Suppress("UnsafeCastFromDynamic")

package com.zi.support.web

import com.zi.support.shared.ApiAppDef
import com.zi.support.shared.ApiCatalog
import com.zi.support.shared.ApiEndpointDef
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement

// More > API: every endpoint the supported apps call (ApiCatalog, shared with Android/iOS), tested like Swagger
// through the checkApiEndpoint Cloud Function so server credentials never reach the browser. GETs run anywhere;
// writes only run against Test (the function enforces that too).
internal object ApiHealthScreen {
    private var environment = "production"
    private var serverId = ApiCatalog.servers.first().id
    private var appIndex = 0
    private var endpointIndex = 0
    private var search = ""
    private var lastResponse: dynamic = null
    private var responseTab = "body"
    /** "app-endpoint" -> "good" | "warn" | "bad" | "busy", plus "HTTP 200 · 120 ms" for the list. */
    private val states = mutableMapOf<String, String>()
    private val summaries = mutableMapOf<String, String>()
    private val latencies = mutableMapOf<String, Int>()
    /** Reads queued by "Check all", for the progress bar. */
    private var runTotal = 0
    private var runDone = 0

    private val app: ApiAppDef get() = ApiCatalog.apps[appIndex]
    private val endpoint: ApiEndpointDef get() = app.endpoints[endpointIndex]
    private fun baseUrl(): String? = ApiCatalog.servers.first { it.id == serverId }
        .let { if (environment == "test") it.testBaseUrl else it.productionBaseUrl }
    private fun key(a: Int, e: Int) = "$a-$e"

    fun render() {
        element("content")?.innerHTML = """
          $MORE_BACK
          <section class="api-top">
            <div class="api-top-title"><h2>API health</h2><code class="api-base" id="api-base"></code></div>
            <div class="api-top-controls">
              <div class="segmented api-env" role="tablist" aria-label="Environment">
                <button data-env="production" role="tab">Production</button><button data-env="test" role="tab">Test</button>
              </div>
              <label class="api-server"><span class="visually-hidden">Server</span><select id="api-server" class="input">${ApiCatalog.servers.joinToString("") { "<option value=\"${it.id}\">${escape(it.label)}</option>" }}</select></label>
              <button class="btn btn-primary api-run-all" id="api-run-all"><span class="material-symbols-rounded" aria-hidden="true">bolt</span><span id="api-run-all-label"></span></button>
            </div>
          </section>
          <section class="api-stats" id="api-overview" aria-live="polite"></section>
          <nav class="api-apps" id="api-app-tabs" role="tablist" aria-label="App">${ApiCatalog.apps.mapIndexed { i, a -> "<button data-app=\"$i\" role=\"tab\"><i class=\"api-dot\" id=\"api-app-dot-$i\" aria-hidden=\"true\"></i>${escape(a.name)}<small>${a.endpoints.size}</small></button>" }.joinToString("")}</nav>
          <section class="api-workbench">
            <aside class="api-sidebar">
              <div class="search-shell"><span class="material-symbols-rounded" aria-hidden="true">search</span><label class="visually-hidden" for="api-search">Search endpoints</label><input id="api-search" class="input search" placeholder="Filter endpoints…"></div>
              <div class="api-tally" id="api-tally"></div>
              <div class="api-endpoints" id="api-endpoints"></div>
            </aside>
            <section class="api-console" id="api-console" aria-live="polite"></section>
          </section>
        """.trimIndent()
        bindMoreBack()
        each("[data-env]") { el -> el.addEventListener("click", { environment = el.getAttribute("data-env")!!; resetResults(); paintHeader(); paintEndpoints(); paintConsole() }) }
        element("api-server")?.addEventListener("change", { event ->
            serverId = (event.target as org.w3c.dom.HTMLSelectElement).value; resetResults(); paintHeader(); paintEndpoints(); paintConsole()
        })
        each("[data-app]") { el -> el.addEventListener("click", { selectApp(el.getAttribute("data-app")!!.toInt()) }) }
        (element("api-search") as HTMLInputElement).addEventListener("input", { event ->
            search = (event.target as HTMLInputElement).value.trim().lowercase(); paintEndpoints()
        })
        click("api-run-all") { runAll() }
        paintHeader(); paintEndpoints(); paintConsole()
    }

    private fun selectApp(index: Int) {
        appIndex = index; endpointIndex = 0; lastResponse = null
        paintHeader(); paintEndpoints(); paintConsole()
        element("api-endpoints")?.scrollTop = 0.0
    }

    private fun paintHeader() {
        each("[data-env]") { it.classList.toggle("active", it.getAttribute("data-env") == environment) }
        (element("api-server") as? org.w3c.dom.HTMLSelectElement)?.value = serverId
        each("[data-app]") { it.classList.toggle("active", it.getAttribute("data-app") == appIndex.toString()) }
        val base = baseUrl()
        element("api-base")?.textContent = base ?: "No Test server is set up for this company yet."
        element("api-base")?.classList?.toggle("missing", base == null)
        element("api-run-all-label")?.textContent = "Check ${app.name}"
        (element("api-run-all") as? HTMLElement)?.asDynamic()?.disabled = base == null
        paintTally()
    }

    /** Results belong to one server + environment; switching starts afresh. */
    private fun resetResults() { states.clear(); summaries.clear(); latencies.clear(); runTotal = 0; runDone = 0 }

    /** Health across every app: healthy / slow / failing / not run, average latency, and progress while "Check all" runs. */
    private fun paintOverview() {
        val total = ApiCatalog.apps.sumOf { it.endpoints.size }
        val good = states.values.count { it == "good" }; val warn = states.values.count { it == "warn" }; val bad = states.values.count { it == "bad" }
        val avg = latencies.values.takeIf { it.isNotEmpty() }?.average()?.toInt()
        val running = runTotal > 0 && runDone < runTotal
        fun stat(value: String, label: String, tone: String, icon: String) =
            "<div class=\"api-stat $tone\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">$icon</span><div><strong>$value</strong><small>$label</small></div></div>"
        element("api-overview")?.innerHTML =
            stat("$good", "Healthy", "good", "check_circle") + stat("$warn", "Slow", "warn", "schedule") + stat("$bad", "Failing", "bad", "error") +
            stat("${total - good - warn - bad}", "Not run", "", "radio_button_unchecked") + stat(if (avg == null) "—" else "$avg ms", "Avg response", "info", "speed") +
            (if (running) "<div class=\"api-progress\" role=\"progressbar\" aria-valuemin=\"0\" aria-valuemax=\"$runTotal\" aria-valuenow=\"$runDone\"><i style=\"width:${runDone * 100 / runTotal}%\"></i><small>Checking $runDone of $runTotal…</small></div>" else "")
        ApiCatalog.apps.indices.forEach { a ->
            val mine = ApiCatalog.apps[a].endpoints.indices.mapNotNull { states[key(a, it)] }.filter { it != "busy" }
            element("api-app-dot-$a")?.className = "api-dot " + when { mine.isEmpty() -> ""; "bad" in mine -> "bad"; "warn" in mine -> "warn"; else -> "good" }
        }
    }

    private fun paintTally() {
        paintOverview()
        val keys = app.endpoints.indices.map { key(appIndex, it) }
        val good = keys.count { states[it] == "good" || states[it] == "warn" }
        val bad = keys.count { states[it] == "bad" }
        element("api-tally")?.innerHTML = if (good + bad == 0) "<span>${app.endpoints.size} endpoints · ${app.endpoints.count { it.method == "GET" }} reads</span>"
        else "<span class=\"good\">$good passing</span><span class=\"bad\">$bad failing</span><span>${app.endpoints.size - good - bad} not run</span>"
    }

    private fun paintEndpoints() {
        val list = element("api-endpoints") ?: return
        val rows = app.endpoints.withIndex().filter { (_, e) -> search.isEmpty() || "${e.method} ${e.path} ${e.label}".lowercase().contains(search) }
        fun row(i: Int, e: ApiEndpointDef): String {
            val k = key(appIndex, i)
            return "<button class=\"api-endpoint ${if (i == endpointIndex) "active" else ""}\" data-endpoint=\"$i\">" +
                "<span class=\"api-method m-${e.method.lowercase()}\">${e.method}</span>" +
                "<span class=\"api-endpoint-text\"><strong>${escape(e.label)}</strong><small>${escape(summaries[k] ?: e.path)}</small></span>" +
                "<span class=\"api-dot ${states[k].orEmpty()}\" aria-hidden=\"true\"></span></button>"
        }
        val (reads, writes) = rows.partition { (_, e) -> e.method == "GET" }
        fun group(title: String, items: List<IndexedValue<ApiEndpointDef>>) =
            if (items.isEmpty()) "" else "<h4 class=\"api-group\">$title<span>${items.size}</span></h4>" + items.joinToString("") { (i, e) -> row(i, e) }
        list.innerHTML = if (rows.isEmpty()) "<p class=\"api-empty\">No endpoints match “${escape(search)}”.</p>"
            else group("Reads", reads) + group("Writes · Test only", writes)
        each("[data-endpoint]") { el -> el.addEventListener("click", {
            endpointIndex = el.getAttribute("data-endpoint")!!.toInt(); lastResponse = null; paintEndpoints(); paintConsole()
        }) }
    }

    private fun paintConsole() {
        val console = element("api-console") ?: return
        val e = endpoint
        val params = ApiCatalog.pathParams(e.path)
        val allowed = ApiCatalog.isAllowed(e.method, environment)
        val writes = e.method != "GET"
        console.innerHTML = """
          <header class="api-console-head"><div><h3>${escape(e.label)}</h3><small>${escape(app.name)}</small></div></header>
          <div class="api-request-line"><span class="api-method m-${e.method.lowercase()}">${e.method}</span><code>${escape(e.path)}</code></div>
          ${if (!allowed) "<div class=\"api-locked\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">lock</span><span>${e.method} changes data, so it only runs on <strong>Test</strong>. <button class=\"link\" id=\"api-to-test\">Switch to Test</button></span></div>" else ""}
          <div class="api-fields">
            ${params.joinToString("") { p -> "<label>Path · ${escape(p)}<input class=\"input\" data-param=\"${escape(p)}\" placeholder=\"${escape(p)}\"></label>" }}
            ${if (writes) "<label class=\"api-body-field\">JSON body<textarea class=\"input\" id=\"api-body\" spellcheck=\"false\" rows=\"7\">{\n  \n}</textarea></label>" else ""}
          </div>
          <details class="api-advanced"><summary><span class="material-symbols-rounded" aria-hidden="true">tune</span>Advanced<small>extra query, authorization</small></summary>
            <div class="api-fields">
              <label>Extra query<input class="input" id="api-query" placeholder="page=1&amp;page_size=20"></label>
              <label>Authorization <small>replaces the service account</small><input class="input" id="api-auth" placeholder="Bearer eyJ…" autocomplete="off"></label>
            </div>
          </details>
          <div class="api-send-row"><button class="btn btn-primary" id="api-send" ${if (!allowed || baseUrl() == null) "disabled" else ""}><span class="material-symbols-rounded" aria-hidden="true">send</span>Send request</button><small>Ctrl / ⌘ + Enter</small></div>
          <div class="api-response" id="api-response">${responseHtml()}</div>
        """.trimIndent()
        click("api-to-test") { environment = "test"; paintHeader(); paintConsole() }
        click("api-send") { send() }
        console.asDynamic().onkeydown = { event: dynamic ->
            if ((event.ctrlKey as Boolean || event.metaKey as Boolean) && event.key == "Enter") { event.preventDefault(); send() }
        }
        bindResponse()
    }

    private fun send() {
        val e = endpoint
        val base = baseUrl()
        if (base == null || !ApiCatalog.isAllowed(e.method, environment)) return
        val values = mutableMapOf<String, String>()
        each("[data-param]") { values[it.getAttribute("data-param")!!] = (it as HTMLInputElement).value }
        val path = ApiCatalog.buildPath(e.path, values, (element("api-query") as? HTMLInputElement)?.value.orEmpty())
        val body = (element("api-body") as? HTMLTextAreaElement)?.value.orEmpty()
        val auth = (element("api-auth") as? HTMLInputElement)?.value.orEmpty()
        element("api-response")?.innerHTML = "<div class=\"api-waiting\"><span class=\"api-spinner\"></span>Sending ${e.method} to ${escape(base)}…</div>"
        (element("api-send") as? HTMLElement)?.asDynamic()?.disabled = true
        call(appIndex, endpointIndex, path, body, auth) { data ->
            lastResponse = data; responseTab = "body"
            (element("api-send") as? HTMLElement)?.asDynamic()?.disabled = false
            element("api-response")?.innerHTML = responseHtml(); bindResponse()
        }
    }

    /** Sends one request through the function and records its result for the list and tally. */
    private fun call(a: Int, e: Int, path: String, body: String, auth: String, done: (dynamic) -> Unit) {
        val k = key(a, e)
        states[k] = "busy"; refreshState(a)
        val request = json("serverId" to serverId, "environment" to environment, "path" to path,
            "method" to ApiCatalog.apps[a].endpoints[e].method, "body" to body, "authorization" to auth)
        promise(FirebaseGateway.callable("checkApiEndpoint", request), { result ->
            val data = result.data
            val ok = data.ok as? Boolean ?: false
            val latency = (data.latencyMs as? Number)?.toInt() ?: 0
            val status = (data.status as? Number)?.toInt() ?: 0
            states[k] = if (!ok) "bad" else if (latency > 2000) "warn" else "good"
            if (ok && latency > 0) latencies[k] = latency else latencies.remove(k)
            summaries[k] = if (status == 0) field(data, "error").ifBlank { "No response" } else "HTTP $status · $latency ms"
            refreshState(a); done(data)
        }, { message ->
            states[k] = "bad"; summaries[k] = message; latencies.remove(k); refreshState(a)
            done(json("ok" to false, "status" to 0, "error" to message))
        })
    }

    private fun refreshState(a: Int) {
        if (a == appIndex) { paintEndpoints(); paintTally() } else paintOverview()
    }


    /** Every read without placeholders in the selected app, three at a time. */
    private fun runAll() {
        if (baseUrl() == null) return
        val a = appIndex
        val queue = ArrayDeque(ApiCatalog.apps[a].endpoints.withIndex().filter { (_, e) -> e.method == "GET" && ApiCatalog.pathParams(e.path).isEmpty() }.map { it.index })
        runTotal = queue.size; runDone = 0; paintOverview()
        fun next() { val e = queue.removeFirstOrNull() ?: return; call(a, e, ApiCatalog.apps[a].endpoints[e].path, "", "") { runDone++; paintOverview(); next() } }
        repeat(3) { next() }
    }

    private fun responseHtml(): String {
        val data = lastResponse ?: return "<div class=\"api-placeholder\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">terminal</span>Send the request to see the response here.</div>"
        val status = (data.status as? Number)?.toInt() ?: 0
        val ok = data.ok as? Boolean ?: false
        val latency = (data.latencyMs as? Number)?.toInt() ?: 0
        val text = field(data, "body").ifBlank { field(data, "preview") }
        val headers: dynamic = data.headers
        val headerRows = if (headers == null) "" else (js("Object").keys(headers) as Array<String>).joinToString("") { "<dt>${escape(it)}</dt><dd>${escape(field(headers, it))}</dd>" }
        val statusText = if (status == 0) field(data, "error").ifBlank { "No response" } else "$status ${field(data, "statusText")}".trim()
        return """
          <div class="api-response-head"><span class="api-status ${if (ok) "ok" else "fail"}">$statusText</span><span>$latency ms</span><span>${formatBytes(text.length)}${if (data.truncated == true) " (cut at 200 KB)" else ""}</span>
            <div class="segmented api-response-tabs"><button data-rtab="body" class="${if (responseTab == "body") "active" else ""}">Body</button><button data-rtab="headers" class="${if (responseTab == "headers") "active" else ""}">Headers</button></div>
            <button class="btn btn-ghost api-copy" id="api-copy"><span class="material-symbols-rounded" aria-hidden="true">content_copy</span>Copy</button></div>
          ${if (responseTab == "headers") "<dl class=\"api-headers\">${headerRows.ifBlank { "<dt>No headers</dt><dd></dd>" }}</dl>"
            else "<pre class=\"api-json\">${highlight(pretty(text)).ifBlank { "<span class=\"j-muted\">Empty body</span>" }}</pre>"}
        """.trimIndent()
    }

    private fun bindResponse() {
        each("[data-rtab]") { el -> el.addEventListener("click", { responseTab = el.getAttribute("data-rtab")!!; element("api-response")?.innerHTML = responseHtml(); bindResponse() }) }
        click("api-copy") {
            val data = lastResponse ?: return@click
            window.navigator.asDynamic().clipboard?.writeText(pretty(field(data, "body").ifBlank { field(data, "preview") }))
            toast("Response copied.")
        }
    }

    private fun pretty(text: String): String = try { js("JSON").stringify(js("JSON").parse(text), null, 2) as String } catch (_: Throwable) { text }

    private val token = Regex("(\"(?:\\\\.|[^\"\\\\])*\")(\\s*:)?|\\b(true|false|null)\\b|-?\\d+(?:\\.\\d+)?(?:[eE][+-]?\\d+)?")

    /** JSON syntax colouring; every piece is HTML-escaped. */
    private fun highlight(text: String): String = buildString {
        var last = 0
        for (m in token.findAll(text)) {
            append(escape(text.substring(last, m.range.first)))
            val cls = when { m.groupValues[2].isNotEmpty() -> "j-key"; m.groupValues[1].isNotEmpty() -> "j-str"; m.groupValues[3].isNotEmpty() -> "j-lit"; else -> "j-num" }
            append("<span class=\"$cls\">${escape(m.groupValues[1].ifEmpty { m.value })}</span>")
            if (m.groupValues[2].isNotEmpty()) append(escape(m.groupValues[2]))
            last = m.range.last + 1
        }
        append(escape(text.substring(last)))
    }

    private fun formatBytes(chars: Int) = if (chars < 1024) "$chars B" else "${(chars / 102.4).toInt() / 10.0} KB"

    private fun each(selector: String, action: (HTMLElement) -> Unit) {
        val nodes = document.querySelectorAll(selector)
        for (i in 0 until nodes.length) (nodes.item(i) as? HTMLElement)?.let(action)
    }
}
